import { EditOutlined } from '@mui/icons-material';
import { DesignSchemeView } from 'components/DesignSchemeView/DesignSchemeView';
import { Panel } from 'components/Panel/Panel';
import { ShrinkToIconButton } from 'components/ShrinkToIconButton/ShrinkToIconButton';
import { ReactElement, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SpansAndLoads, Loads, LoadType, BaseAudit } from 'types/domainTypes';
import { LoadsPanel } from './LoadsPanel';
import { LoadTabs } from './LoadTabs';
import useLoads from 'hooks/Loads/LoadsHook';

interface SpansAndLoadsViewProps {
  spansAndLoads: SpansAndLoads & BaseAudit;
  onEditClick: () => void;
  isWallCalculation: boolean;
}

export const isLoadEmpty = (load?: Loads): boolean => {
  if (!load) {
    return true;
  }

  return !load.selfWeight && !load.uniformLoad && load.distributedLoads?.length === 0 && load.pointLoads?.length === 0;
};

const allLoadsEmpty = ({
  permanentLoads,
  snowLoads,
  pressureLoads,
  suctionLoads,
  imposedLoads,
  constructionLoads,
  excepSnowLoads,
}: SpansAndLoads): boolean => {
  for (const load of [permanentLoads, snowLoads, pressureLoads, suctionLoads, imposedLoads, constructionLoads, excepSnowLoads]) {
    if (!isLoadEmpty(load)) {
      return false;
    }
  }
  return true;
};

export function SpansAndLoadsView({ spansAndLoads, onEditClick, isWallCalculation }: SpansAndLoadsViewProps): ReactElement | null {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState<LoadType>(isWallCalculation ? LoadType.PressureLoad : LoadType.PermanentLoad);
  const { loads, label } = useLoads(tabValue, spansAndLoads);

  const onTabChange = (newValue: LoadType) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (!isLoadEmpty(spansAndLoads.permanentLoads)) {
      setTabValue(LoadType.PermanentLoad);
    } else if (!isLoadEmpty(spansAndLoads.snowLoads)) {
      setTabValue(LoadType.SnowLoad);
    } else if (!isLoadEmpty(spansAndLoads.pressureLoads)) {
      setTabValue(LoadType.PressureLoad);
    } else if (!isLoadEmpty(spansAndLoads.suctionLoads)) {
      setTabValue(LoadType.SuctionLoad);
    } else if (!isLoadEmpty(spansAndLoads.imposedLoads)) {
      setTabValue(LoadType.ImposedLoad);
    } else if (!isLoadEmpty(spansAndLoads.constructionLoads)) {
      setTabValue(LoadType.ConstructionLoad);
    } else if (!isLoadEmpty(spansAndLoads.excepSnowLoads)) {
      setTabValue(LoadType.ExcepSnowLoad);
    } else {
      setTabValue(LoadType.PressureLoad);
    }
  }, [spansAndLoads]);

  const panelButton = (
    <ShrinkToIconButton buttonText={t('calculation-page.edit-spans-and-loads-button')} icon={<EditOutlined />} onClick={onEditClick} />
  );

  return (
    <Panel
      data-testid="spans-and-loads-panel"
      panelTitle={t('calculation-page.label-spans-and-loads')}
      panelSummary={t('common.summary-format', {
        createdBy: spansAndLoads.createdByName ? spansAndLoads.createdByName : spansAndLoads.createdBy,
        modifiedOn: spansAndLoads.modifiedOn,
        modifiedBy: spansAndLoads.modifiedByName ? spansAndLoads.modifiedByName : spansAndLoads.modifiedBy,
      })}
      panelButton={panelButton}
    >
      {!allLoadsEmpty(spansAndLoads) && (
        <LoadTabs tabValue={tabValue} onTabChange={onTabChange} isWallCalculation={isWallCalculation} spansAndLoads={spansAndLoads} />
      )}
      <DesignSchemeView
        style={{
          marginLeft: '-8px',
          width: `calc(100% + 32px)`,
        }}
        spansAndLoads={spansAndLoads}
        loadType={tabValue}
      />
      {!isLoadEmpty(loads) && (
        <LoadsPanel
          loadLabel={label}
          tableHeading={t('calculation-page.label-loads')}
          loadType={tabValue}
          loads={loads}
          sx={{ marginLeft: '-24px', width: `calc(100% + 48px)` }}
        />
      )}
    </Panel>
  );
}
